import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FiMessageCircle,
  FiHeart,
  FiMoreHorizontal,
  FiX,
  FiZap,
} from "react-icons/fi";
import { FaFire } from "react-icons/fa";
import { GiRose } from "react-icons/gi";
import { MdExplore } from "react-icons/md";
import { useAppSelector } from "../../hooks/useAppStore";
import { selectUnseenLikesCount } from "../../store/slices/likesSlice";
import { selectConversations } from "../../store/slices/messagingSlice";

export function BottomNav() {
  const unseenLikes = useAppSelector(selectUnseenLikesCount);
  const conversations = useAppSelector(selectConversations);
  const unreadCount = conversations.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();

  // When opening: make visible immediately, then animate in
  // When closing: animate out (drawerOpen=false triggers translate-y-full), then hide after transition
  const openDrawer = () => {
    setDrawerVisible(true);
    // Allow the element to mount before triggering the transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setDrawerOpen(true));
    });
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    // Wait for the 300ms CSS transition to finish before hiding the element
    setTimeout(() => setDrawerVisible(false), 300);
  };

  const navItems: { to: string; label: string; Icon: React.ElementType; badge: number }[] = [
    { to: "/home", label: "Discover", Icon: MdExplore, badge: 0 },
    { to: "/likes", label: "Likes", Icon: FiHeart, badge: unseenLikes },
    { to: "/messages", label: "Messages", Icon: FiMessageCircle, badge: unreadCount },
  ];

  const drawerItems = [
    { to: "/boosts", label: "Boost", Icon: FiZap, color: "text-yellow" },
    { to: "/kings-and-queens", label: "K&Q", Icon: FaFire, color: "text-magenta" },
    { to: "/roses", label: "Roses", Icon: GiRose, color: "text-pink-400" },
  ];

  return (
    <>
      {/* Drawer overlay */}
      {drawerVisible && (
        <div
          className={`fixed inset-0 z-40 sm:hidden transition-opacity duration-300 ${drawerOpen ? "opacity-100" : "opacity-0"} bg-black/50`}
          onClick={closeDrawer}
        />
      )}

      {/* More drawer — slides up from bottom nav */}
      {drawerVisible && (
        <div
          className={`fixed left-0 right-0 z-50 sm:hidden transition-transform duration-300 ${
            drawerOpen ? "translate-y-0" : "translate-y-full"
          }`}
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 64px)" }}
        >
          <div className="bg-charcoal border border-white/10 rounded-t-2xl mx-2 shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <span className="text-offwhite/40 text-xs font-semibold uppercase tracking-widest">More</span>
              <button
                onClick={closeDrawer}
                aria-label="Close menu"
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/5 text-offwhite/40 hover:text-offwhite"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 px-4 pb-5 pt-2">
              {drawerItems.map(({ to, label, Icon, color }) => (
                <button
                  key={label}
                  onClick={() => {
                    closeDrawer();
                    navigate(to);
                  }}
                  className="flex flex-col items-center gap-2 py-4 px-2 rounded-2xl bg-white/5 hover:bg-white/10 active:bg-white/15 transition-colors"
                >
                  <Icon className={`w-6 h-6 ${color}`} />
                  <span className="text-offwhite text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav bar */}
      <nav aria-label="Primary" className="fixed bottom-0 left-0 right-0 z-40 bg-charcoal/95 backdrop-blur-md border-t border-white/10 pb-safe sm:hidden">
        <div className="flex items-center justify-around max-w-lg mx-auto">
          {navItems.map(({ to, label, Icon, badge }) => (
            <NavLink
              key={label}
              to={to}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 px-2 text-xs font-body transition-colors duration-200 min-w-[60px]
                ${isActive ? "text-yellow" : "text-offwhite/40 hover:text-offwhite/80"}`
              }
            >
              <div className="relative">
                <Icon className="w-5 h-5" />
                {badge > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-lg bg-magenta text-white text-[9px] font-bold flex items-center justify-center">
                    <span aria-hidden="true">{badge > 9 ? "9+" : badge}</span>
                    <span className="sr-only">{badge} new</span>
                  </span>
                )}
              </div>
              <span>{label}</span>
            </NavLink>
          ))}

          {/* More button */}
          <button
            onClick={() => (drawerOpen ? closeDrawer() : openDrawer())}
            aria-expanded={drawerOpen}
            aria-haspopup="menu"
            className={`flex flex-col items-center gap-1 py-3 px-2 text-xs font-body transition-colors duration-200 min-w-[60px] ${
              drawerOpen ? "text-yellow" : "text-offwhite/40 hover:text-offwhite/80"
            }`}
          >
            <FiMoreHorizontal className="w-5 h-5" />
            <span>More</span>
          </button>
        </div>
      </nav>
    </>
  );
}
